/**
 * Keeps cart attribute `active_location` in sync with the selected store.
 * Values: windsor | halifax | mississauga (for cart transforms / pricing).
 * On location change: updates the attribute and refreshes the cart UI.
 */
if (!window.Maximize?.loadedScript?.includes("active-location-cart.js")) {
  if (window.Maximize?.loadedScript) {
    window.Maximize.loadedScript.push("active-location-cart.js");
  }

  (function () {
    const ATTR_KEY = "active_location";
    const CART_UPDATED = "maximize:cart:updated";
    const VALID_LOCATIONS = ["windsor", "halifax", "mississauga"];

    let syncPromise = null;
    let lastWritten = null;
    let selfUpdating = false;

    function normalize(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/&amp;/g, "&")
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    }

    /** Map store name / market handle → cart attribute value. */
    function resolveActiveLocation(storeName) {
      const s = normalize(storeName);
      if (!s) return null;

      if (VALID_LOCATIONS.includes(s)) return s;

      if (s.includes("mississauga")) return "mississauga";
      if (s.includes("windsor")) return "windsor";
      if (s.includes("halifax")) return "halifax";

      if (s === "mississauga location" || s.includes("mississauga location")) {
        return "mississauga";
      }

      return null;
    }

    function getSelectedStoreName(explicit) {
      if (explicit != null && String(explicit).trim() !== "") {
        return String(explicit).trim();
      }
      try {
        if (window.Alpine && Alpine.store("xStoreMap")?.currentStoreSelected) {
          return Alpine.store("xStoreMap").currentStoreSelected;
        }
      } catch (_) {}
      try {
        const saved = JSON.parse(localStorage.getItem("currentStoreSelected") || "null");
        return saved?.selectedStore || "";
      } catch (_) {
        return "";
      }
    }

    function getSectionsToRender() {
      try {
        const helper = window.xCartHelper || (window.Alpine && Alpine.store("xCartHelper"));
        if (!helper?.getSectionsToRender) return [];

        if (window.Maximize?.isCartPage) {
          const sectionId = document.querySelector("#MainCart__Items")?.dataset?.sectionId;
          return helper.getSectionsToRender(sectionId) || [];
        }

        const cartDrawerEl = document.querySelector("#CartDrawer");
        const sectionId = cartDrawerEl?.dataset?.sectionId;
        return helper.getSectionsToRender(sectionId) || [];
      } catch (_) {
        return [];
      }
    }

    function applySections(sectionsToRender, response) {
      if (!response?.sections || typeof getSectionInnerHTML !== "function") return;

      sectionsToRender.forEach((section) => {
        const sectionEl = document.querySelector(section.selector);
        if (!sectionEl || !response.sections[section.id]) return;

        const html = getSectionInnerHTML(response.sections[section.id], section.selector);

        if (section.selector === "#CartDrawer") {
          const wrapperVisible = sectionEl
            .querySelector("#CartDrawer__Wrapper")
            ?.classList.contains("is-visible");
          const upsellEl = sectionEl.querySelector("#CartDrawer__Upsell");
          if (upsellEl) upsellEl.remove();
          sectionEl.innerHTML = html;
          if (wrapperVisible) {
            sectionEl.querySelector("#CartDrawer__Wrapper")?.classList.add("is-visible");
          }
          if (upsellEl) {
            const container = sectionEl.querySelector("#CartDrawer__Container");
            if (container) container.after(upsellEl);
          }
          return;
        }

        if (section.selector === "#MainCart__Items") {
          sectionEl.classList.add("disable-fade-element");
        }
        sectionEl.innerHTML = html;
      });
    }

    async function fetchCart() {
      const res = await fetch((window.Shopify?.routes?.root || "/") + "cart.js", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`cart.js failed: ${res.status}`);
      return res.json();
    }

    /**
     * Write active_location and optionally re-render cart sections (transforms / prices).
     */
    async function writeActiveLocation(location, { refreshUi = true } = {}) {
      const sectionsToRender = refreshUi ? getSectionsToRender() : [];
      const sections = [...new Set(sectionsToRender.map((s) => s.id).filter(Boolean))];

      const res = await fetch((window.Shopify?.routes?.root || "/") + "cart/update.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          attributes: {
            [ATTR_KEY]: location,
          },
          sections,
        }),
      });
      if (!res.ok) throw new Error(`cart/update.js failed: ${res.status}`);

      const updated = await res.json();
      lastWritten = location;

      if (refreshUi) {
        applySections(sectionsToRender, updated);
        notifyCartUpdated(location, updated);
      }

      return updated;
    }

    function notifyCartUpdated(location, cart) {
      selfUpdating = true;
      try {
        document.dispatchEvent(
          new CustomEvent(CART_UPDATED, {
            bubbles: true,
            detail: {
              reason: "active-location",
              active_location: location,
              cart,
              openMiniCart: false,
            },
          })
        );
        document.dispatchEvent(
          new CustomEvent("maximize:active-location:updated", {
            detail: { active_location: location, cart },
          })
        );
      } finally {
        selfUpdating = false;
      }
    }

    /**
     * Ensure cart.attributes.active_location matches the selected store.
     * When the value changes, refresh cart UI so transforms/pricing update.
     * @param {string} [storeName]
     * @param {{ forceRefresh?: boolean }} [opts]
     */
    function ensureActiveLocation(storeName, opts = {}) {
      const location = resolveActiveLocation(getSelectedStoreName(storeName));
      if (!location) {
        console.log("[active-location] no resolvable store yet; skip cart attribute sync");
        return Promise.resolve(null);
      }

      if (syncPromise) return syncPromise;

      syncPromise = (async () => {
        try {
          const cart = await fetchCart();
          const current = cart?.attributes?.[ATTR_KEY] || "";
          const changed = current !== location;

          if (!changed && !opts.forceRefresh) {
            lastWritten = location;
            console.log("[active-location] cart already has", ATTR_KEY, location);
            return cart;
          }

          console.log(
            "[active-location] updating",
            ATTR_KEY,
            current || "(empty)",
            "→",
            location,
            changed ? "(location changed — refreshing cart)" : "(force refresh)"
          );

          return await writeActiveLocation(location, { refreshUi: true });
        } catch (err) {
          console.error("[active-location] sync failed", err);
          return null;
        } finally {
          syncPromise = null;
        }
      })();

      return syncPromise;
    }

    // Page load — sync attribute only (refresh UI only if value had to change)
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => ensureActiveLocation());
    } else {
      ensureActiveLocation();
    }

    // Store selected / changed → update property; cart UI refreshes when value changes
    document.addEventListener("maximize:store-changed", (e) => {
      const store = e?.detail?.store;
      if (!store) return;
      ensureActiveLocation(store);
    });

    // After other cart mutations, re-check attribute (skip our own updates)
    document.addEventListener(CART_UPDATED, (e) => {
      if (selfUpdating || e?.detail?.reason === "active-location") return;
      ensureActiveLocation();
    });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") ensureActiveLocation();
    });

    window.Maximize = window.Maximize || {};
    window.Maximize.activeLocation = {
      ATTR_KEY,
      VALID_LOCATIONS,
      resolve: resolveActiveLocation,
      ensure: ensureActiveLocation,
      getLastWritten: () => lastWritten,
    };
  })();
}
